import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Store, Building2, Fuel, Landmark, Home, Building } from 'lucide-react';

const industries = [
  {
    icon: Store,
    title: 'Retail & Showrooms',
    description: 'Eye-catching signage for retail stores, malls, and showrooms to attract customers.',
  },
  {
    icon: Building2,
    title: 'Corporate Offices',
    description: 'Professional branding solutions for reception areas and corporate environments.',
  },
  {
    icon: Fuel,
    title: 'Petrol Pump Industry',
    description: 'Durable and compliant signage solutions for fuel stations across India.',
  },
  {
    icon: Landmark,
    title: 'Government Projects',
    description: 'Large-scale signage projects for government institutions and public spaces.',
  },
  {
    icon: Home,
    title: 'Builders & Real Estate',
    description: 'Premium signage for construction sites, residential projects, and property branding.',
  },
  {
    icon: Building,
    title: 'Commercial Complexes',
    description: 'Comprehensive wayfinding and branding for commercial buildings and complexes.',
  },
];

const IndustryCard = ({ industry, index }) => {
  const Icon = industry.icon;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className="group relative p-6 md:p-8 bg-white border border-gray-100 rounded-2xl hover:border-sky-200 hover:shadow-elevated transition-all duration-500"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`industry-card-${index}`}
    >
      <div className="relative z-10">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-100 to-violet-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-6 h-6 text-sky-500" />
        </div>
        <h3 className="text-lg md:text-xl font-['Oswald'] font-bold text-slate-800 mb-2 group-hover:text-sky-500 transition-colors duration-300">
          {industry.title}
        </h3>
        <p className="text-sm text-slate-500 font-['Manrope']">
          {industry.description}
        </p>
      </div>
    </motion.div>
  );
};

const IndustriesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="industries"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
      data-testid="industries-section"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-sky-50 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-violet-50 rounded-full blur-3xl opacity-50"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
            Industries We Serve
          </span>
          <h2 className="section-heading mb-6">
            SIGNAGE FOR <span className="gradient-text">EVERY INDUSTRY</span>
          </h2>
          <p className="section-subheading mx-auto">
            We bring our expertise to diverse industries, delivering customized signage 
            solutions that meet specific business needs across Gujarat.
          </p>
        </motion.div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {industries.map((industry, index) => (
            <IndustryCard key={index} industry={industry} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
